import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from '../base.entity';
import { ChatParticipantRole, ChatParticipantType } from './chat-enums';

/**
 * Membership record. One row per (conversation × participant). Active
 * participants have isActive=true; leaving or being removed flips this
 * rather than deleting the row, so message history still resolves the
 * sender's display name correctly.
 *
 * participantType is the hard boundary between audiences. The
 * MembershipService rejects:
 *   - portal_user added to dm or group
 *   - internal_user paired with portal_user in a dm (DMs are internal-only)
 *   - more than one portal_user in a customer_support conversation
 */
@Entity('chat_participant')
@Index('idx_chat_participant_conversation', ['conversationId', 'isActive'])
@Index('idx_chat_participant_user', ['userId', 'isActive'])
@Index('uq_chat_participant_user_convo', ['conversationId', 'userId'], {
	unique: true,
})
export class ChatParticipantEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_chat_conversation__id',
		unsigned: true,
	})
	public conversationId!: number;

	@Column('enum', {
		name: 'participantType',
		enum: ChatParticipantType,
	})
	public participantType!: ChatParticipantType;

	/**
	 * For internal_user: references _user._id (instance userId).
	 * For portal_user: references the tenant's public_user record (same
	 *   _user table, different publicInstanceId scoping).
	 * Null for external_sms / external_email / system.
	 */
	@Column('int', {
		name: 'fk_user__id',
		nullable: true,
		unsigned: true,
	})
	public userId?: number;

	/** Phone number for external_sms, email address for external_email. */
	@Column('varchar', {
		name: 'externalIdentifier',
		nullable: true,
		length: 255,
	})
	public externalIdentifier?: string;

	@Column('enum', {
		name: 'role',
		enum: ChatParticipantRole,
		default: ChatParticipantRole.Member,
	})
	public role!: ChatParticipantRole;

	/**
	 * Override display name. Used for:
	 *   - external_* participants (no user record to pull from)
	 *   - system participants ("SupplyMover AI", "Automation")
	 *   - custom nicknames within a group (future)
	 */
	@Column('varchar', { name: 'displayName', nullable: true, length: 255 })
	public displayName?: string;

	@Column('datetime', { name: 'joinedAt', precision: 3 })
	public joinedAt!: Date;

	/**
	 * `null` is part of the type so a rejoin can CLEAR the stamp via an
	 * entity-path save. TypeORM skips `undefined` properties entirely,
	 * so `leftAt = undefined` silently no-ops the clear and leaves an
	 * active participant carrying the timestamp of a departure they
	 * already reversed. Same reasoning as `topic` on the conversation.
	 */
	@Column('datetime', { name: 'leftAt', nullable: true, precision: 3 })
	public leftAt?: Date | null;

	@Column('tinyint', {
		name: 'isActive',
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean;

	@Column('tinyint', {
		name: 'notificationsMuted',
		width: 1,
		default: () => "'0'",
	})
	public notificationsMuted!: boolean;

	/** Short-lived typing indicator timestamp — Redis holds the hot path, this is just for last-seen. */
	@Column('datetime', { name: 'lastTypedAt', nullable: true, precision: 3 })
	public lastTypedAt?: Date;

	@Column('json', { name: 'metadata', nullable: true })
	public metadata?: Record<string, unknown>;
}
